// Internal modules
import { getLocale } from '@/lib/globals';
import { getAddressMetaKey } from '@/lib/utils/miscUtils';
import { formatGriffinAddressMetadata } from '@/lib/utils/responseMappers';

const NORTH_AMERICA = ['CA', 'US'];
const getTerritoryRegion = ({ code, region }) => {
  return region === 'LATAM' || NORTH_AMERICA.includes(code)
    ? 'AMERICAS'
    : region;
};

const mapGriffinMetadata = localeMetadata => {
  const { currencyCode, phone } = localeMetadata;
  const { masks, patterns } = phone;
  const { default: pattern } = patterns;
  const { country } = getLocale();
  const addressKey = getAddressMetaKey();
  const address = localeMetadata.address
    ? { [addressKey]: formatGriffinAddressMetadata(localeMetadata.address) }
    : {};

  return {
    address,
    currencyCode,
    dateMeta: localeMetadata.date,
    phoneMeta: {
      mask: masks.mobile,
      // ⬇ Getting rid of ^ at index 0 and $ at last index
      pattern: pattern.substring(1, pattern.length - 1),
    },
    ...mapTerritories(localeMetadata.territories, country),
  };
};

/**
 * @function mapTerritories
 * @param {array} territories
 * @param {string} countryCode
 * @returns {object}
 * @description Maps the response from `localeMetadata.territories`
 */
const mapTerritories = territories => {
  const { locale } = getLocale();
  const defaultTerritories = {
    dialingCodeSelectOptions: [],
    territories: { [locale]: [] },
    territoriesByRegion: {
      [locale]: {
        AMERICAS: [],
        APAC: [],
        EMEA: [],
      },
    },
    territoriesToDialingCodeMap: {},
  };

  if (!Array.isArray(territories)) {
    return defaultTerritories;
  }

  return territories.reduce((accum, territory) => {
    const { code, internationalDialingCode, name } = territory;

    // dialingCodeSelectOptions
    accum.dialingCodeSelectOptions.push({
      key: code,
      text: `${name} +${internationalDialingCode}`,
      value: code,
    });

    // territories
    accum.territories[locale].push({ key: code, text: name, value: code });

    // territoriesToDialingCodeMap
    accum.territoriesToDialingCodeMap[code] = `${internationalDialingCode}`;

    // territoriesByRegion
    const region = getTerritoryRegion(territory);
    accum.territoriesByRegion[locale][region].push(territory);

    return accum;
  }, defaultTerritories);
};

export { mapGriffinMetadata, mapTerritories };
